import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, Copy, Eye, EyeOff, GitCompareArrows, Info, Layers, ListChecks, Lock, Play, Search, Timer, Trash2, Variable } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';

const TerraformIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [exampleWorkspace, setExampleWorkspace] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [organization, setOrganization] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [notificationToken, setNotificationToken] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [connectApiToken, setConnectApiToken] = useState('');
  const [connectOrganization, setConnectOrganization] = useState('');
  const [connectBaseUrl, setConnectBaseUrl] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/terraform`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getTerraformStatus();
      if (data?.connected) {
        setConnected(true);
        setOrganization(data.organization || '');
        setBaseUrl(data.base_url || '');
        setNotificationToken(data.notification_token || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectTerraform({
        api_token: connectApiToken,
        organization: connectOrganization.trim(),
        base_url: connectBaseUrl.trim() || undefined,
      });
      setSuccessMsg('Terraform Cloud connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectTerraform();
      setConnected(false);
      setSuccessMsg('Terraform Cloud disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testTerraform();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Terraform Cloud (${data.workspace_count ?? 0} workspace${data.workspace_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={terraformIcon} alt="Terraform Cloud" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Terraform Cloud
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Queue plan and destroy runs, apply or discard pending runs, read state outputs, manage variables, detect drift, react to run failures, and run AI investigations against Terraform Cloud</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium truncate">{organization}</p>
                </div>
              )}
              {baseUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">API URL</span>
                  <p className="font-medium truncate">{baseUrl}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Notification Events Received</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Notification</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notifications Webhook</CardTitle>
            <CardDescription>Add a notification configuration per workspace to trigger workflows on run and drift events. Actions work without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyValue('url', webhookUrl)} className="gap-1 shrink-0">
                  {copied === 'url' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {notificationToken && (
              <div>
                <Label className="text-sm">Notification Token</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{showToken ? notificationToken : '••••••••••••••••••••••••'}</code>
                  <Button variant="outline" size="sm" onClick={() => setShowToken(s => !s)} className="gap-1 shrink-0" aria-label={showToken ? 'Hide token' : 'Show token'}>
                    {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyValue('token', notificationToken)} className="gap-1 shrink-0">
                    {copied === 'token' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'token' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Notifications are configured per workspace, not in Organization Settings. In Terraform Cloud, go to <strong className="text-foreground">Workspaces</strong>, open the workspace you want events from, then in that workspace's sidebar open <strong className="text-foreground">Settings → Notifications</strong> and click <strong className="text-foreground">Create a Notification</strong>. Choose the <strong className="text-foreground">Webhook</strong> destination, set the URL above, paste the token, and select the run events you care about (Created, Planning, Needs Attention, Applying, Completed, Errored). If your workspace has health assessments enabled (HCP Terraform Plus/Premium), a <strong className="text-foreground">Health Events</strong> section also appears — select Drifted and Check Failed there to enable drift triggers. Repeat for each workspace. Terraform Cloud signs each delivery with HMAC-SHA512 (<code className="bg-muted px-1 rounded text-[11px] font-mono">X-TFE-Notification-Signature</code> header) and Kestrel verifies it.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Terraform Cloud actions and triggers in your workflows to automate plan/apply gating, drift response, and failure investigation.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (webhook)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Run Needs Attention', desc: 'Triggers when a run has a plan waiting for confirmation.', Icon: AlertTriangle },
                  { name: 'Run Completed / Errored', desc: 'Triggers when a run finishes, successfully or with errors.', Icon: CheckCircle2 },
                  { name: 'Run Created / Planning / Applying', desc: 'Triggers on run lifecycle transitions.', Icon: Play },
                  { name: 'Drift Detected', desc: 'Triggers when a health assessment detects infrastructure drift.', Icon: GitCompareArrows },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Create Run / Destroy Run', desc: 'Queue a plan or destroy run on a workspace, optionally auto-applying.', Icon: Play },
                  { name: 'Apply / Discard / Cancel Run', desc: 'Confirm, discard, or cancel a pending or in-progress run.', Icon: ListChecks },
                  { name: 'Wait for Run', desc: 'Poll a run until it reaches a terminal state and return the result.', Icon: Timer },
                  { name: 'Get State Outputs', desc: 'Read the workspace\u2019s current state outputs for downstream steps.', Icon: Info },
                  { name: 'Lock / Unlock Workspace', desc: 'Lock a workspace during maintenance or force-unlock a stuck one.', Icon: Lock },
                  { name: 'Set Variable', desc: 'Create or update a Terraform or environment variable on a workspace.', Icon: Variable },
                  { name: 'Check Drift', desc: 'Read the latest health assessment result for drift.', Icon: GitCompareArrows },
                  { name: 'Investigate Terraform', desc: 'AI investigates workspaces, runs, variables, and drift (read-only).', Icon: Search },
                  { name: 'List Workspaces / Runs / Variables', desc: 'Enumerate org workspaces, recent runs, and workspace variables.', Icon: Layers },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Terraform Cloud run needs attention in the{' '}
              <input
                value={exampleWorkspace}
                onChange={e => setExampleWorkspace(e.target.value)}
                placeholder="workspace name"
                style={{ width: `${Math.max(exampleWorkspace.length || 14, 14)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}workspace, post the plan summary to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack and ask for approval, then apply the run if approved or discard it if rejected.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleWorkspace || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Terraform Cloud run needs attention in the ${exampleWorkspace} workspace, post the plan summary to ${exampleChannel} on Slack and ask for approval, then apply the run if approved or discard it if rejected.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={terraformIcon} alt="Terraform Cloud" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Terraform Cloud</CardTitle>
                <CardDescription>Connect Terraform Cloud (HCP Terraform) to queue and gate runs, read state outputs, manage variables, react to run failures and drift, and run AI investigations in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="terraform-token" className="text-sm">API Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the Terraform Cloud API v2 with a <strong className="text-foreground">team or user API token</strong>. Create a team token under <strong className="text-foreground">Organization Settings → API Tokens → Team Tokens</strong> (recommended), or a user token under <strong className="text-foreground">Account Settings → Tokens</strong>.
              </p>
              <Input
                id="terraform-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="Terraform Cloud API token"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Terraform Cloud API.
              </p>
            </div>
            <div>
              <Label htmlFor="terraform-org" className="text-sm">Organization</Label>
              <Input
                id="terraform-org"
                value={connectOrganization}
                onChange={e => setConnectOrganization(e.target.value)}
                placeholder="my-org"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The Terraform Cloud organization name, as shown in the URL: <code className="bg-muted px-1 rounded text-[11px] font-mono">app.terraform.io/app/&lt;organization&gt;</code>.
              </p>
            </div>
            <div>
              <Label htmlFor="terraform-base-url" className="text-sm">API URL (optional)</Label>
              <Input
                id="terraform-base-url"
                value={connectBaseUrl}
                onChange={e => setConnectBaseUrl(e.target.value)}
                placeholder="https://app.terraform.io/api/v2"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for Terraform Cloud. For Terraform Enterprise, set your instance's API base URL, e.g. <code className="bg-muted px-1 rounded text-[11px] font-mono">https://tfe.example.com/api/v2</code>.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken || !connectOrganization.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Terraform Cloud
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default TerraformIntegration;
